import React, { useEffect } from 'react';
import classNames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { tracker } from '@fiverr-private/listing_lib';
import { EXPERIMENTS } from '@fiverr-private/listing_experiments';
import { trackBrazeEvent, trackSearchPerseusPageLoad } from '../utils/gtmTracking/gtmTracking';
import { ListingsPerseusPropTypes } from '../types';
import { FLOWS } from '../utils/constants';
import Store from '../store';
import { AppError, ErrorBoundary, ErrorPage } from './common';
import SubCategories from './subcategories/SubCategories';
import Search from './search/Search';
import { BusinessGigsListing } from './business_listings/BusinessGigsListing';
import { BriefFloatingFormEntry, useBffRowObserver } from './bff/BriefFloatingFormEntry';
import './index.scss';
import './common/listings/index.scss';

const ListingsComponentMapper = {
    [FLOWS.CATEGORY]: SubCategories,
    [FLOWS.SEARCH]: Search,
    [FLOWS.BUSINESS_SEARCH]: BusinessGigsListing,
    [FLOWS.BUSINESS_CATEGORIES]: BusinessGigsListing,
    [FLOWS.PROFESSION_CATEGORY]: BusinessGigsListing,
};

const ListingsPerseus = ({
    displayData,
    breadcrumbs,
    topBarSorting,
    appData = {},
    activeFilters = {},
    appFilters = {},
    knownCrawler,
    listings,
    listingAttributes,
    flowName,
    requestContext,
    tracking,
    errorContext,
    dataLayerObject,
    assumedLanguage,
    isBusiness,
    isBusinessUser,
    userData,
    currency,
    rollouts,
    activeExperiments = {},
    clientExperimentsGroups = {},
    experimentsToAllocate = {},
    shouldShowHourlyRateBanners,
    shouldShowExpressDelivery,
    shouldShowPromoteBanner,
    showMediumBucketsModalities,
    logoMakerConfig,
    subCategoryData,
    vettedGigsCount,
    managedAccount,
    assistantData,
    pushkinConfig,
    storeBuilder,
    isProfessionCategory = false,
    seoData,
    isFallbackFromExpertListings,
    priceUpfrontConfiguration,
    priceUpfrontContext,
    isPriceUpfrontEnabled,
    priceUpfrontBuyableFeePolicy,
    similarTimezoneCountries,
    suggestions,
}) => {
    Object.assign(appData, {
        isBusiness,
        isBusinessUser,
        activeExperiments,
        clientExperimentsGroups,
    });

    useEffect(() => {
        if (!errorContext) {
            trackSearchPerseusPageLoad(dataLayerObject);
            tracker.view();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const { userId } = getContext();
        const { query, categoryIds: { subCategoryId } = {} } = requestContext;

        if (userId && listings) {
            if (subCategoryId) {
                trackBrazeEvent({
                    brazeEventName: 'sub_category_visit',
                    brazeEventProperties: { sub_category_id: subCategoryId },
                });
            } else if (query) {
                trackBrazeEvent({
                    brazeEventName: 'search_performed',
                    brazeEventProperties: { term: query },
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    tracker.initialize(tracking);

    const { onImpression: bffOnImpression, bffEntryProps } = useBffRowObserver();

    useEffect(() => {
        const shouldInit = assistantData?.enable && assistantData?.shouldShowAssistant;

        if (!shouldInit || !pushkinConfig) {
            return;
        }

        const initRealtimeListener = async () => {
            const { RealtimeListener } = await import(
                /* webpackChunkName: 'listings/assistant-realtime' */ '@fiverr-private/assistant_realtime'
            );
            await RealtimeListener.init(pushkinConfig);
        };

        void initRealtimeListener();
    }, [assistantData?.enable, assistantData?.shouldShowAssistant, pushkinConfig]);

    if (errorContext) {
        return <ErrorPage currency={currency} />;
    }

    const {
        sorting,
        user: { loggedIn, userCountry },
        flow,
        abTests,
        baseUrl,
    } = appData;
    const { abTests: contextAbTests } = getContext();
    const inCrowsMpBffExpansionExperiment =
        !isBusiness && Number(contextAbTests[EXPERIMENTS.CROWS_MP_BFF_EXPANSION]) === 2;
    const Component = ListingsComponentMapper[flow];
    const listingsPerseusClassNames = classNames('listings-perseus');
    const storeProps = {
        knownCrawler,
        listings,
        appData,
        rollouts,
        appFilters,
        activeFilters,
        requestContext,
        displayData,
        userData,
        currency,
        logoMakerConfig,
        subCategoryData,
        experimentsToAllocate,
        assistantData,
        listingAttributes,
        assumedLanguage,
        shouldShowHourlyRateBanners,
        shouldShowExpressDelivery,
        shouldShowPromoteBanner,
        showMediumBucketsModalities,
        storeBuilder,
        isFallbackFromExpertListings,
        priceUpfrontConfiguration,
        priceUpfrontContext,
        isPriceUpfrontEnabled,
        priceUpfrontBuyableFeePolicy,
        similarTimezoneCountries,
        suggestions,
    };

    return (
        <div className={listingsPerseusClassNames}>
            <Store {...storeProps}>
                <ErrorBoundary fallback={AppError}>
                    <Component
                        loggedIn={loggedIn}
                        userCountry={userCountry}
                        sorting={sorting}
                        breadcrumbs={breadcrumbs}
                        topBarSorting={topBarSorting.map((opt) => ({ ...opt, selected: opt.id === sorting.sortBy }))}
                        abTests={abTests}
                        vettedGigsCount={vettedGigsCount}
                        baseUrl={baseUrl}
                        managedAccount={managedAccount}
                        flow={flow}
                        flowName={flowName}
                        rollouts={rollouts}
                        assistantData={assistantData}
                        isProfessionCategory={isProfessionCategory}
                        seoData={seoData}
                        rowObservers={[bffOnImpression]}
                    />
                    {inCrowsMpBffExpansionExperiment && (
                        <BriefFloatingFormEntry {...bffEntryProps} resultsCount={appData?.pagination.total} />
                    )}
                </ErrorBoundary>
            </Store>
        </div>
    );
};

ListingsPerseus.propTypes = ListingsPerseusPropTypes;

export default ListingsPerseus;
